"use client";

import { useState, useEffect, useCallback } from "react";
import { useParams, useApiReady } from "@/hooks";
import { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClientSubscriptions,
  createClientSubscription,
  updateClientSubscription,
  deleteClientSubscription,
  destroyClientSubscription,
  pauseClientSubscription,
  activateClientSubscription,
  deleteClientSubscriptionViaPut,
  markClientSubscriptionExpired,
  generatePaymentLink,
  tryChargeSubscription,
  tryChargeSubscriptionWithCard,
  fetchSubscribersStats,
} from "@/store/modules/clientSubscriptions/thunks";
import { fetchClients, fetchClientsAll } from "@/store/modules/clients/thunks";
import { fetchSubscriptionsAll } from "@/store/modules/SubscriptionsAdmin/thunks";
import { fetchSubscriptionSummary } from "@/store/modules/SubscriptionsAdmin/thunks";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { subscriptionSchema } from "@/validations";
import { usePermission } from "../usePermission/usePermission";

const useClientSubscription = (
  onSuccess?: (result?: any) => void,
  establishmentSlug?: string,
) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { per_page, current_page } = params;
  const { hasPermission, loading: loadingPermission } = usePermission();
  const canReadSubscribers = hasPermission("read subscribers");

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    watch,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(subscriptionSchema),
    defaultValues: {
      client_id: "",
      subscription_id: "",
      expiration_date: null,
    },
  });

  const setFormData = (
    data: Partial<typeof subscriptionSchema.__outputType>,
  ) => {
    reset(data);
  };

  const dispatch: AppDispatch = useDispatch();
  const [subscriptionId, setSubscriptionId] = useState<any>();
  const [clientSlug, setClientSlug] = useState<any>();
  const [clientSubscriptionId, setClientSubscriptionId] = useState<any>();
  const [paymentMethod, setPaymnetMethod] = useState<any>();

  // Estados para validação de clientes
  const [clientsForSelect, setClientsForSelect] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);

  // Estados para validação de assinaturas
  const [subscriptionsForSelect, setSubscriptionsForSelect] = useState<any[]>(
    [],
  );
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);

  const fetchClientsForSelect = useCallback(async () => {
    setLoadingClients(true);
    try {
      // Buscar até 500 clientes e filtrar apenas os clientes que NÃO possuem assinaturas
      const response = await dispatch(
        fetchClientsAll({
          without_pagination: 1,
          per_page: 500,
        } as any),
      );

      if (fetchClientsAll.fulfilled.match(response)) {
        const allClients = response.payload.data || [];
        // Filtra somente clientes sem subscriptions (ou com array vazia)
        const available = allClients.filter(
          (c: any) => !c.subscriptions || c.subscriptions.length === 0,
        );
        setClientsForSelect(allClients);
      }
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
      setClientsForSelect([]);
    } finally {
      setLoadingClients(false);
    }
  }, [dispatch]);

  const fetchSubscriptionsForSelect = useCallback(async () => {
    setLoadingSubscriptions(true);
    try {
      // Usar o novo thunk fetchSubscriptionsAll para buscar todas as assinaturas sem paginação
      const response = await dispatch(
        fetchSubscriptionsAll({
          without_pagination: 1,
        }),
      );

      if (fetchSubscriptionsAll.fulfilled.match(response)) {
        setSubscriptionsForSelect(response.payload.data || []);
      }
    } catch (error) {
      console.error("Erro ao buscar assinaturas:", error);
      setSubscriptionsForSelect([]);
    } finally {
      setLoadingSubscriptions(false);
    }
  }, [dispatch]);

  const onSubmit = async (data: typeof subscriptionSchema.__outputType) => {
    try {
      let result;
      let isSuccess = false;

      if (
        !hasPermission("create subscribers") &&
        !hasPermission("update subscribers")
      ) {
        return;
      }

      if (clientSubscriptionId) {
        result = await dispatch(
          updateClientSubscription({
            subscription_id: data.subscription_id,
            slug: clientSubscriptionId,
            data,
          }),
        );
        isSuccess = updateClientSubscription.fulfilled.match(result);
      } else {
        result = await dispatch(createClientSubscription({ data }));
        isSuccess = createClientSubscription.fulfilled.match(result);
      }

      if (isSuccess) {
        // Pass the thunk result payload to onSuccess so the caller can open details modal
        if (onSuccess) onSuccess(result?.payload ?? result);
        await dispatch(fetchClientSubscriptions({ params }));
        reset();
        setSubscriptionId(null);
        setClientSubscriptionId(null);
        // Return created result to caller for immediate follow-up actions
        return result?.payload ?? result;
      }
    } catch (error) {
      console.error("Erro ao enviar dados:", error);
    }
  };

  const subscribers = useSelector(
    (state: any) => state.clientSubscription.clientSubscriptions.data,
  );
  const loading = useSelector((state: any) => state.clientSubscription.loading);
  const error = useSelector((state: any) => state.clientSubscription.error);
  const paymentLinkResult = useSelector(
    (state: any) => state.clientSubscription.paymentLink,
  );
  const isSubmittingPaymentMethod = useSelector(
    (state: any) => state.clientSubscription.isSubmittingPaymentMethod,
  );
  const clientSubscriptions = useSelector(
    (state: any) => state.clientSubscription.clientSubscriptions,
  );
  const subscribersStats = useSelector(
    (state: any) => state.clientSubscription.subscribersStats,
  );
  const statsLoading = useSelector(
    (state: any) => state.clientSubscription.statsLoading,
  );

  const { isReady: isApiReady } = useApiReady(2000);

  useEffect(() => {
    if (!isApiReady || !canReadSubscribers) return;
    const timeoutId = setTimeout(
      () => {
        dispatch(fetchClientSubscriptions({ establishmentSlug, params }));
      },
      params.search && params.search.length > 0 ? 500 : 0,
    );

    return () => clearTimeout(timeoutId);
  }, [isApiReady, params, establishmentSlug, canReadSubscribers, dispatch]);

  const totalItems = clientSubscriptions?.total ?? 0;
  const itemsPerPage = clientSubscriptions?.per_page ?? 10;
  const totalPages = clientSubscriptions?.last_page ?? 1;

  const [currentPage, setCurrentPage] = useState(
    clientSubscriptions?.current_page ?? 1,
  );

  useEffect(() => {
    if (
      clientSubscriptions?.current_page &&
      clientSubscriptions.current_page !== currentPage
    ) {
      setCurrentPage(clientSubscriptions.current_page);
    }
  }, [clientSubscriptions?.current_page]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handlePage(page);
  };

  const handleFilterChange = (value: string) => {
    updateParams({
      search: value,
      current_page: 1, // reset pagina p/ 1 ao filtrar
    });
    setCurrentPage(1);
  };

  const handleDelete = async (opts?: { skipRefresh?: boolean }) => {
    try {
      if (subscriptionId) {
        const result = await dispatch(
          deleteClientSubscription({
            subscription_id: subscriptionId,
            slug: clientSlug,
          }),
        );
        if (deleteClientSubscription.fulfilled.match(result)) {
          if (!opts?.skipRefresh) {
            dispatch(fetchClientSubscriptions({ establishmentSlug, params }));
            try {
              await dispatch(fetchSubscriptionSummary());
              if (establishmentSlug) {
                await fetchStats(String(establishmentSlug));
              }
            } catch (e) {
              console.warn(
                "Falha ao atualizar resumo/estatísticas após excluir:",
                e,
              );
            }
          }
        }
        setSubscriptionId(null);
        return true;
      }
    } catch (error) {
      console.error("Erro ao excluir subscription:", error);
    }
  };

  // Explicit-ID operations for bulk flows
  const deleteByIds = async (
    explicitSubscriptionId: any,
    explicitClientSlug: any,
    opts?: { skipRefresh?: boolean },
  ) => {
    try {
      if (explicitSubscriptionId) {
        const result = await dispatch(
          deleteClientSubscription({
            subscription_id: explicitSubscriptionId,
            slug: explicitClientSlug,
          }),
        );
        if (deleteClientSubscription.fulfilled.match(result)) {
          if (!opts?.skipRefresh) {
            dispatch(fetchClientSubscriptions({ establishmentSlug, params }));
            try {
              await dispatch(fetchSubscriptionSummary());
              if (establishmentSlug) {
                await fetchStats(String(establishmentSlug));
              }
            } catch (e) {
              console.warn(
                "Falha ao atualizar resumo/estatísticas após excluir:",
                e,
              );
            }
          }
        }
        return true;
      }
    } catch (error) {
      console.error("Erro ao excluir subscription (ids explícitos):", error);
    }
  };

  const destroyByIds = async (
    explicitSubscriptionId: any,
    explicitClientSlug: any,
    opts?: { skipRefresh?: boolean },
  ) => {
    try {
      if (explicitSubscriptionId) {
        const result = await dispatch(
          destroyClientSubscription({
            subscription_id: explicitSubscriptionId,
            slug: explicitClientSlug,
          }),
        );
        if (destroyClientSubscription.fulfilled.match(result)) {
          if (!opts?.skipRefresh) {
            dispatch(fetchClientSubscriptions({ establishmentSlug, params }));
            try {
              await dispatch(fetchSubscriptionSummary());
              if (establishmentSlug) {
                await fetchStats(String(establishmentSlug));
              }
            } catch (e) {
              console.warn(
                "Falha ao atualizar resumo/estatísticas após destruir:",
                e,
              );
            }
          }
        }
        return true;
      }
    } catch (error) {
      console.error("Erro ao destruir subscription (ids explícitos):", error);
    }
  };

  const pauseByIds = async (
    explicitSubscriptionId: any,
    explicitClientSlug: any,
    opts?: { skipRefresh?: boolean },
  ) => {
    try {
      if (explicitSubscriptionId && hasPermission("update subscribers")) {
        const result = await dispatch(
          pauseClientSubscription({
            subscription_id: explicitSubscriptionId,
            slug: explicitClientSlug,
          }),
        );
        if (pauseClientSubscription.fulfilled.match(result)) {
          if (!opts?.skipRefresh) {
            dispatch(fetchClientSubscriptions({ establishmentSlug, params }));
            try {
              await dispatch(fetchSubscriptionSummary());
              if (establishmentSlug) {
                await fetchStats(String(establishmentSlug));
              }
            } catch (e) {
              console.warn(
                "Falha ao atualizar resumo/estatísticas após pausar:",
                e,
              );
            }
          }
        }
        return true;
      }
    } catch (error) {
      console.error("Erro ao pausar subscription (ids explícitos):", error);
    }
  };

  const activateByIds = async (
    explicitSubscriptionId: any,
    explicitClientSlug: any,
    opts?: { skipRefresh?: boolean },
  ) => {
    try {
      if (explicitSubscriptionId && hasPermission("update subscribers")) {
        const result = await dispatch(
          activateClientSubscription({
            subscription_id: explicitSubscriptionId,
            slug: explicitClientSlug,
          }),
        );
        if (activateClientSubscription.fulfilled.match(result)) {
          if (!opts?.skipRefresh) {
            dispatch(fetchClientSubscriptions({ establishmentSlug, params }));
            try {
              await dispatch(fetchSubscriptionSummary());
              if (establishmentSlug) {
                await fetchStats(String(establishmentSlug));
              }
            } catch (e) {
              console.warn(
                "Falha ao atualizar resumo/estatísticas após ativar:",
                e,
              );
            }
          }
        }
        return true;
      }
    } catch (error) {
      console.error("Erro ao ativar subscription (ids explícitos):", error);
    }
  };

  const deletePutByIds = async (
    explicitSubscriptionId: any,
    explicitClientSlug: any,
    opts?: { skipRefresh?: boolean },
  ) => {
    try {
      if (explicitSubscriptionId && hasPermission("update subscribers")) {
        const result = await dispatch(
          deleteClientSubscriptionViaPut({
            subscription_id: explicitSubscriptionId,
            slug: explicitClientSlug,
          }),
        );
        if (deleteClientSubscriptionViaPut.fulfilled.match(result)) {
          if (!opts?.skipRefresh) {
            dispatch(fetchClientSubscriptions({ establishmentSlug, params }));
            try {
              await dispatch(fetchSubscriptionSummary());
              if (establishmentSlug) {
                await fetchStats(String(establishmentSlug));
              }
            } catch (e) {
              console.warn(
                "Falha ao atualizar resumo/estatísticas após excluir PUT:",
                e,
              );
            }
          }
        }
        return true;
      }
    } catch (error) {
      console.error("Erro ao excluir via PUT (ids explícitos):", error);
    }
  };

  const markExpiredByIds = async (
    explicitSubscriptionId: any,
    explicitClientSlug: any,
    opts?: { skipRefresh?: boolean },
  ) => {
    try {
      if (explicitSubscriptionId && hasPermission("update subscribers")) {
        const result = await dispatch(
          markClientSubscriptionExpired({
            subscription_id: explicitSubscriptionId,
            slug: explicitClientSlug,
          }),
        );
        if (markClientSubscriptionExpired.fulfilled.match(result)) {
          if (!opts?.skipRefresh) {
            dispatch(fetchClientSubscriptions({ establishmentSlug, params }));
            try {
              await dispatch(fetchSubscriptionSummary());
              if (establishmentSlug) {
                await fetchStats(String(establishmentSlug));
              }
            } catch (e) {
              console.warn(
                "Falha ao atualizar resumo/estatísticas após marcar vencida:",
                e,
              );
            }
          }
        }
        return true;
      }
    } catch (error) {
      console.error("Erro ao marcar vencida (ids explícitos):", error);
    }
  };

  const handleDestroy = async (opts?: { skipRefresh?: boolean }) => {
    try {
      if (subscriptionId) {
        const result = await dispatch(
          destroyClientSubscription({
            subscription_id: subscriptionId,
            slug: clientSlug,
          }),
        );
        if (destroyClientSubscription.fulfilled.match(result)) {
          if (!opts?.skipRefresh) {
            dispatch(fetchClientSubscriptions({ establishmentSlug, params }));
            try {
              await dispatch(fetchSubscriptionSummary());
              if (establishmentSlug) {
                await fetchStats(String(establishmentSlug));
              }
            } catch (e) {
              console.warn(
                "Falha ao atualizar resumo/estatísticas após destruir:",
                e,
              );
            }
          }
        }
        setSubscriptionId(null);
        return true;
      }
    } catch (error) {
      console.error("Erro ao excluir definitivamente subscription:", error);
    }
  };

  const handlePause = async (opts?: { skipRefresh?: boolean }) => {
    try {
      if (subscriptionId && hasPermission("update subscribers")) {
        const result = await dispatch(
          pauseClientSubscription({
            subscription_id: subscriptionId,
            slug: clientSlug,
          }),
        );
        if (pauseClientSubscription.fulfilled.match(result)) {
          if (!opts?.skipRefresh) {
            dispatch(fetchClientSubscriptions({ establishmentSlug, params }));
            try {
              await dispatch(fetchSubscriptionSummary());
              if (establishmentSlug) {
                await fetchStats(String(establishmentSlug));
              }
            } catch (e) {
              console.warn(
                "Falha ao atualizar resumo/estatísticas após pausar:",
                e,
              );
            }
          }
        }
        setSubscriptionId(null);
        return true;
      }
    } catch (error) {
      console.error("Erro ao pausar subscription:", error);
    }
  };

  const handleActivate = async (opts?: { skipRefresh?: boolean }) => {
    try {
      if (subscriptionId && hasPermission("update subscribers")) {
        const result = await dispatch(
          activateClientSubscription({
            subscription_id: subscriptionId,
            slug: clientSlug,
          }),
        );
        if (activateClientSubscription.fulfilled.match(result)) {
          if (!opts?.skipRefresh) {
            dispatch(fetchClientSubscriptions({ establishmentSlug, params }));
            try {
              await dispatch(fetchSubscriptionSummary());
              if (establishmentSlug) {
                await fetchStats(String(establishmentSlug));
              }
            } catch (e) {
              console.warn(
                "Falha ao atualizar resumo/estatísticas após ativar:",
                e,
              );
            }
          }
        }
        setSubscriptionId(null);
        return true;
      }
    } catch (error) {
      console.error("Erro ao ativar subscription:", error);
    }
  };

  const handleDeletePut = async (opts?: { skipRefresh?: boolean }) => {
    try {
      if (subscriptionId && hasPermission("update subscribers")) {
        const result = await dispatch(
          deleteClientSubscriptionViaPut({
            subscription_id: subscriptionId,
            slug: clientSlug,
          }),
        );
        if (deleteClientSubscriptionViaPut.fulfilled.match(result)) {
          if (!opts?.skipRefresh) {
            dispatch(fetchClientSubscriptions({ establishmentSlug, params }));
            try {
              await dispatch(fetchSubscriptionSummary());
              if (establishmentSlug) {
                await fetchStats(String(establishmentSlug));
              }
            } catch (e) {
              console.warn(
                "Falha ao atualizar resumo/estatísticas após excluir PUT:",
                e,
              );
            }
          }
        }
        setSubscriptionId(null);
        return true;
      }
    } catch (error) {
      console.error("Erro ao excluir via PUT:", error);
    }
  };

  const handleMarkExpired = async (opts?: { skipRefresh?: boolean }) => {
    try {
      if (subscriptionId && hasPermission("update subscribers")) {
        const result = await dispatch(
          markClientSubscriptionExpired({
            subscription_id: subscriptionId,
            slug: clientSlug,
          }),
        );
        if (markClientSubscriptionExpired.fulfilled.match(result)) {
          if (!opts?.skipRefresh) {
            dispatch(fetchClientSubscriptions({ establishmentSlug, params }));
            try {
              await dispatch(fetchSubscriptionSummary());
              if (establishmentSlug) {
                await fetchStats(String(establishmentSlug));
              }
            } catch (e) {
              console.warn(
                "Falha ao atualizar resumo/estatísticas após marcar vencida:",
                e,
              );
            }
          }
        }
        setSubscriptionId(null);
        return true;
      }
    } catch (error) {
      console.error("Erro ao marcar como vencida:", error);
    }
  };

  const paymentLink = async (customer: any) => {
    try {
      if (subscriptionId && hasPermission("create payment subscription")) {
        const result = await dispatch(
          generatePaymentLink({
            subscription_id: subscriptionId,
            client_sub_id: customer?.id,
            payment_method: paymentMethod,
            customer: { ...customer },
          }),
        );
        return result;
      }
    } catch (error) {
      console.error("Erro ao excluir subscription:", error);
    }
  };

  const tryCharge = async () => {
    if (subscriptionId && hasPermission("create payment subscription")) {
      const result = await dispatch(
        tryChargeSubscription({
          subscription_id: subscriptionId,
          client_sub_id: clientSubscriptionId,
        }),
      );
      return result.payload;
    }
  };

  const tryChargeWithCard = async (
    payload: { card_token_id: number; payment_type: string; customer: any },
    customSubscriptionId?: string,
    customClientSubscriptionId?: number,
  ) => {
    const subId = customSubscriptionId || subscriptionId;
    const clientSubId = customClientSubscriptionId || clientSubscriptionId;

    if (subId && clientSubId && hasPermission("create payment subscription")) {
      const result = await dispatch(
        tryChargeSubscriptionWithCard({
          subscription_id: subId,
          client_sub_id: clientSubId,
          payload,
        }),
      );
      return result.payload;
    } else {
      console.error("IDs faltando para cobrança:", {
        subId,
        clientSubId,
      });
      throw new Error("IDs de assinatura não encontrados");
    }
  };

  // Função para buscar estatísticas
  const fetchStats = async (
    establishmentSlug: string,
    dateParams?: { date_from?: string; date_to?: string },
  ) => {
    try {
      // Ensure required date range params are provided
      const toDateStr = (d: Date) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };
      const today = new Date();
      const finalParams = {
        date_from: dateParams?.date_from || toDateStr(today),
        date_to: dateParams?.date_to || toDateStr(today),
      };

      await dispatch(
        fetchSubscribersStats({
          establishmentSlug,
          params: finalParams,
        }),
      );
    } catch (error) {
      console.error("Erro ao buscar estatísticas:", error);
    }
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    subscribers,
    loading,
    error,
    setSubscriptionId,
    setClientSubscriptionId,
    setClientSlug,
    handleDelete,
    setFormData,
    getValues,
    control,
    setValue,
    handlePause,
    handleActivate,
    handleDeletePut,
    handleMarkExpired,
    paymentLink,
    setPaymnetMethod,
    paymentLinkResult,
    isSubmittingPaymentMethod,
    tryCharge,
    tryChargeWithCard,
    currentPage,
    handlePageChange,
    totalPages,
    itemsPerPage,
    setParams,
    updateParams,
    handleFilterChange,
    params,
    subscribersStats,
    statsLoading,
    fetchStats,
    // Novos campos para validação de clientes
    clientsForSelect,
    loadingClients,
    fetchClientsForSelect,
    // Novos campos para validação de assinaturas
    subscriptionsForSelect,
    loadingSubscriptions,
    fetchSubscriptionsForSelect,
    handleDestroy,
    deleteByIds,
    destroyByIds,
    pauseByIds,
    activateByIds,
    deletePutByIds,
    markExpiredByIds,
  };
};

export default useClientSubscription;
